import { getApp, getApps, initializeApp, deleteApp } from 'firebase/app';
const DEFAULT_APP_NAME = '[DEFAULT]';
const appCache = new Map();
/**
 * Retrieve the cached Firebase app or initialize it when absent. Ensures
 * singleton semantics per app name while warning when the requested config
 * differs from the original initialization.
 */
export function getFirebaseApp(config, appName = DEFAULT_APP_NAME) {
    const cached = appCache.get(appName);
    if (cached) {
        warnOnConfigMismatch(appName, cached.config, config);
        return cached.app;
    }
    const existing = getApps().find((app) => app.name === appName);
    if (existing) {
        appCache.set(appName, { app: existing, config });
        warnOnConfigMismatch(appName, existing.options, config);
        return existing;
    }
    const app = initializeApp(config, appName);
    appCache.set(appName, { app, config });
    return app;
}
/**
 * Alias of {@link getFirebaseApp} for callers that prefer the initialize*
 * naming convention.
 */
export const initializeFirebaseApp = getFirebaseApp;
/**
 * Returns an existing Firebase app instance if one is already registered.
 */
export function getExistingFirebaseApp(appName = DEFAULT_APP_NAME) {
    const cached = appCache.get(appName)?.app;
    if (cached) {
        return cached;
    }
    try {
        return getApp(appName);
    }
    catch {
        return undefined;
    }
}
/**
 * Determines whether a Firebase app has been initialized for the given name.
 */
export function hasFirebaseApp(appName = DEFAULT_APP_NAME) {
    return Boolean(appCache.has(appName) || getApps().some((app) => app.name === appName));
}
/**
 * Removes a cached Firebase app and deletes the underlying instance. Useful in
 * tests where a clean slate is required between runs.
 */
export async function resetFirebaseApp(appName = DEFAULT_APP_NAME) {
    const cached = getExistingFirebaseApp(appName);
    if (!cached) {
        return;
    }
    appCache.delete(appName);
    await deleteApp(cached);
}
function warnOnConfigMismatch(appName, previous, next) {
    if (configsMatch(previous, next)) {
        return;
    }
    console.warn(`[firebase-app] Firebase app "${appName}" already initialized with a different configuration. ` +
        'Subsequent calls will reuse the original instance. Ensure this is intentional.');
}
const CONFIG_KEYS = [
    'apiKey',
    'authDomain',
    'projectId',
    'storageBucket',
    'messagingSenderId',
    'appId',
    'measurementId',
];
function configsMatch(a, b) {
    if (!a || !b) {
        return false;
    }
    return CONFIG_KEYS.every((key) => a[key] === b[key]);
}
export { DEFAULT_APP_NAME };
//# sourceMappingURL=firebase-app.js.map